import { NodeProps, useReactFlow } from "reactflow";
import { operationType, selectDataType } from "../../../../../services/types";
import {
   Main,
   Styled,
   CustomInputSection,
   InputFormatHeader,
   InfoIcon,
   Tooltip,
   ScrollableFieldsContainer,
   FieldContainer,
   FieldHeader,
   ButtonGroup,
   ActionButton,
   FieldGroup
} from "./style";
import React, { useEffect, useState } from "react";
import { updateNodeValue } from "../../../../../services/method";
import { TextInput } from "../../../../form/textInput";
import { Select } from "../../../../form/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";

type InputFieldType = {
   id: string;
   name: string;
   type: 'string' | 'number' | 'boolean';
   value?: string;
};

type startProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function Start({ id, data, setChildMinimize, setChildSettings }: startProps) {
   const { setNodes } = useReactFlow();
   const [startTypes, setStartTypes] = useState<selectDataType[]>([
      { id: 1, name: 'Custom Input', refId: 'customInput', isActive: true },
   ]);

   const [inputFields, setInputFields] = useState<InputFieldType[]>([
      { id: '1', name: '', type: 'string', value: '' }
   ]);

   const [fieldTypes] = useState<selectDataType[]>([
      { id: 1, name: 'string', refId: 'string', isActive: true },
      { id: 2, name: 'number', refId: 'number' },
      { id: 3, name: 'boolean', refId: 'boolean' },
   ]);

   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [showTooltip, setShowTooltip] = React.useState(false);

   // // Check if this Start node is connected to an aiAgent node
   // const isConnectedToAiAgent = () => {
   //     const edges = getEdges();
   //     const nodes = getNodes();

   //     // Find edges where this Start node is the source
   //     const outgoingEdges = edges.filter(edge => edge.source === id);

   //     // Check if any target node is an aiAgent
   //     return outgoingEdges.some(edge => {
   //         const targetNode = nodes.find(node => node.id === edge.target);
   //         return targetNode?.data?.action === 'aiAgent' || targetNode?.data?.action === 'output';
   //     });
   // };

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // --
   useEffect(() => {
      if (!data.value) {
         updateNodeValue(id, setNodes, { startType: startTypes[0]?.refId, custom: inputFields });
      } else {
         setStartTypes((startTypes) => startTypes.map((startType) => {
            return { ...startType, isActive: startType.refId === data.value.startType };
         }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   useEffect(() => {
      if (data?.value?.custom) updateNodeValue(id, setNodes, { custom: inputFields });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [inputFields, id, setNodes]);

   // ---
   const handleSelectRequests = (selectId: number) => {
      setStartTypes((startTypes) => startTypes.map((startType) => {
         if (startType.id === selectId) return { ...startType, isActive: true };
         else return { ...startType, isActive: false };
      }));
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, startType: startTypes.find(m => m.id === selectId)?.refId } } };
         else return node;
      }));
   }

   // ---
   const handleAddField = () => {
      const newField: InputFieldType = {
         id: Date.now().toString(),
         name: `field${inputFields.length + 1}`,
         type: 'string',
         value: ''
      };
      setInputFields(prev => [...prev, newField]);
   }
   // ---
   const handleRemoveField = (fieldId: string) => {
      setInputFields(prev => prev.filter(field => field.id !== fieldId));
   }
   // ---
   const handleFieldNameChange = (fieldId: string, newName: string) => {
      setInputFields(prev => prev.map(field =>
         field.id === fieldId ? { ...field, name: newName } : field
      ));
   }
   // ---
   const handleFieldTypeChange = (fieldId: string, newType: 'string' | 'number' | 'boolean') => {
      setInputFields(prev => prev.map(field =>
         field.id === fieldId ? { ...field, type: newType } : field
      ));
   };
   // ---
   const handleFieldValueChange = (fieldId: string, newValue: string) => {
      setInputFields(prev => prev.map(field =>
         field.id === fieldId ? { ...field, value: newValue } : field
      ));
   }

   // ---
   const isWarning = !data.value?.startType || (data.value?.startType === "customInput" && (!data.value?.custom || data.value.custom.length === 0));

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={false}
            style={{ width: '420px' }}
         >
            <Styled>
               <div>
                  <Select
                     title="Method"
                     data={startTypes}
                     handleSelectItem={(id) => handleSelectRequests(id)}
                     isSmall={true}
                  />
               </div>
               {startTypes.find((s) => s.refId === "customInput" && s.isActive) && (
                  <CustomInputSection>
                     <InputFormatHeader>
                        <h3>Input Format</h3>
                        <InfoIcon
                           onMouseEnter={() => setShowTooltip(true)}
                           onMouseLeave={() => setShowTooltip(false)}
                        >
                           i
                           {showTooltip && (<Tooltip>Define custom input fields with names, types, and test values for your workflow</Tooltip>)}
                        </InfoIcon>
                     </InputFormatHeader>
                     <ScrollableFieldsContainer>
                        {inputFields.map((field, index) => (
                           <FieldContainer key={field.id}>
                              <FieldHeader>
                                 <span>Field {index + 1}</span>
                                 <ButtonGroup>
                                    <ActionButton
                                       className="add-btn"
                                       onClick={handleAddField}
                                    >
                                       +
                                    </ActionButton>
                                    {inputFields.length > 1 && (
                                       <ActionButton
                                          className="remove-btn"
                                          onClick={() => handleRemoveField(field.id)}
                                       >
                                          🗑
                                       </ActionButton>
                                    )}
                                 </ButtonGroup>
                              </FieldHeader>
                              <FieldGroup>
                                 <label>Data Type</label>
                                 <Select
                                    data={fieldTypes.map(type => ({
                                       ...type,
                                       isActive: type.refId === field.type
                                    }))}
                                    handleSelectItem={(typeId) => {
                                       const selectedType = fieldTypes.find(t => t.id === typeId);
                                       if (selectedType) {
                                          handleFieldTypeChange(field.id, selectedType.refId as 'string' | 'number' | 'boolean');
                                       }
                                    }}
                                    isSmall={true}
                                 />
                              </FieldGroup>

                              <FieldGroup>
                                 <label>Field Name</label>
                                 <TextInput
                                    value={field.name}
                                    handleChange={(e) => handleFieldNameChange(field.id, e.target.value)}
                                    placeholder="Enter field name"
                                 />
                              </FieldGroup>



                              <FieldGroup>
                                 <label>Field Value</label>
                                 <TextInput
                                    value={field.value || ''}
                                    handleChange={(e) => handleFieldValueChange(field.id, e.target.value)}
                                    placeholder="Enter test value"
                                    type={field.type === "string" ? "text" : "number"}
                                    max={field.type === "boolean" ? 1 : undefined}
                                    min={field.type === "boolean" ? 0 : undefined}

                                 />
                              </FieldGroup>
                           </FieldContainer>
                        ))}
                     </ScrollableFieldsContainer>
                  </CustomInputSection>
               )}
            </Styled>
         </ExpandedBlock> : null}
      </Main>
   </>);
}